import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	Checkbox,
	CircularProgress,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	// TextField,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { twMerge } from 'tailwind-merge';
import { Add, Circle, CircleOutlined, Close, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ReactPlayer from 'react-player';

import BaseInputValidation from '@/components/ui/Input/BaseInputValidation';
import BaseAutocomplete from '@/components/ui/Select/BaseAutocomplete';
import BaseSelect from '@/components/ui/Select/BaseSelect';
import ProjectRelation from '@/components/Modal/Project/ProjectRelation';
import Loader from '@/components/ui/Loader/Loader';
import BDHelmet from '@/components/BDHelmet';
import Reference from '@/components/ui/Reference/Reference';
import FileExist from '@/components/Modal/Project/FileExist';
import ModalDelete from '@/components/Modal/General/Delete';
import ModalVersion from '@/components/Modal/Project/Version';

import usePatch from '@/hooks/usePatch';
import useVideoUrl from '@/hooks/useVideoUrl';
import { uploadBlob } from '@/helper/uploadBlob';
import { checkFileExist } from '@/helper/checkFileExist';
import { route } from '@/constants/routeName';
import project from '@/api/project';
// import PDFViewer from './PDFViewer';

const schema = yup.object().shape({
	productprefixid: yup.object().required(),
	productcodenumber: yup.string().required(),
	productname: yup.string().required(),
	category: yup.string(),
	// productcomponentflag: yup.string().required(),
	producttype: yup.string().required(),
});

const { createProject: cP, getProduct, getProductGroup, getProductCategory } = project();

export default function Edit() {
	const navigate = useNavigate();
	const params = useParams();
	const { project, id } = params;

	const idProject = project || id;
	const isDuplicate = id && !project;

	const productCategory = useSelector((state) => state.content.productCategory);
	const productPrefix = useSelector((state) => state.content.productPrefix);
	const projectStatus = useSelector((state) => state.content.projectStatusD);
	const privilegeLevels = useSelector((state) => state.content.privilegeLevels);
	const [tb, setTb] = useState(false);
	const [openRelation, setOpenRelation] = useState(false);
	const [filesAttachment, setFilesAttachment] = useState([]);
	const [projectRelations, setProjectRelations] = useState([]);
	const [isFileExist, setIsFileExist] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isChecked, setIsChecked] = useState(false);
	const [isDeleteArticle, setIsDeleteArticle] = useState(false);
	const [isNewVersion, setIsNewVersion] = useState(false);

	const {
		handleSubmit,
		control,
		setValue,
		watch,
		trigger,
		setError,
		clearErrors,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			qrstatus: '0',
			productcomponentflag: 0,
		},
		reValidateMode: 'onChange',
	});

	const dType = watch('productprefixid');
	const number = watch('productcodenumber');
	const ver = watch('productversion');
	const vUrl = watch('producturl');
	const pVer = watch('productversion');
	const isVideo = useMemo(() => dType?.productprefix.toLowerCase().includes('video'), [dType]);
	// const isProj = useMemo(() => dType?.productprefix.toLowerCase().includes('proj') || +dType?.id === 12, [dType]);
	const { videoUrl } = useVideoUrl(dType, isVideo, vUrl);

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'reference',
	});

	const {
		data: p,
		isFetching: isFetchingP,
		isFetched: isFetchedP,
		error,
		isError,
	} = useQuery({
		queryKey: ['project', idProject],
		enabled: !!idProject,
		queryFn: () => getProduct(idProject),
	});

	useEffect(() => {
		if (isError && error?.status === 404) {
			navigate('/', { replace: true });
		}
	}, [isError, error, navigate]);

	useEffect(() => {
		if (p?.data) {
			const {
				productprefix_detail,
				productcodenumber,
				productname,
				category,
				productcomponentflag,
				producttype,
				qrstatus,
				article,
				description,
				productversion,
				attachments,
				projectrelations,
				status,
				producturl,
				referencerelation,
			} = p.data;
			const c = category[0]?.categoryid;
			setValue('productprefixid', {
				productprefixid: productprefix_detail[0].productprefixid,
				productprefix: productprefix_detail[0].productprefix,
			});
			setValue('productcodenumber', productcodenumber);
			setValue('productname', productname);
			setValue('category', c && c !== 'x' ? c : '' || '');
			setValue('article', article?.length ? article.map((a) => a.articlenumber).join(',') : '');
			setValue('description', description || '');
			setValue('productcomponentflag', productcomponentflag || 0);
			setValue('producttype', `${producttype}`);
			setValue('qrstatus', `${qrstatus}`);
			setValue('productversion', productversion);
			setValue('status', +status);
			setValue('producturl', producturl || '');
			if (referencerelation) {
				setValue(
					'reference',
					referencerelation.map((r) => {
						return { ...r };
					})
				);
			}
			setFilesAttachment(
				attachments?.length
					? attachments.map((a) => {
							const priv = a.privilegelevelid?.split(',');
							const isPrivate = a.private === 1;
							if (isPrivate) {
								priv.push('99');
							}
							return {
								...a,
								privilegelevelid: privilegeLevels?.map((p) => {
									return { id: p.privilegelevelid, name: p.privilegelevelname, value: priv?.includes(`${p.privilegelevelid}`) };
								}),
								private: isPrivate,
								file: {
									name: a.linkdatasheet,
								},
								validated: true,
								error: false,
								progress: null,
							};
					  })
					: []
			);

			// Set initial projectRelations, then fetch version_list for each
			const initialRelations = projectrelations.length
				? projectrelations.map((p) => {
						return {
							...p,
							id: p.id,
							productprefix: p.component_prefix_name,
							productcodenumber: p.component_code_number,
							productid: p.componentid,
							productname: p.component_productname,
							selectedVersionProductId: p.componentid, // Default to current componentid
						};
				  })
				: [];

			setProjectRelations(initialRelations);

			// Fetch version_list for each relation
			if (initialRelations.length > 0) {
				const fetchVersionLists = async () => {
					const updatedRelations = await Promise.all(
						initialRelations.map(async (relation) => {
							if (relation.componentid) {
								try {
									const productResponse = await getProduct(relation.componentid);
									let versionList = productResponse?.data?.version_list || [];

									// Ensure current product is in version_list if not already present
									const currentProductInList = versionList.some(v => v.productid === relation.componentid);
									if (!currentProductInList && productResponse?.data) {
										versionList = [
											...versionList,
											{
												productid: relation.componentid,
												productname: productResponse.data.productname || relation.component_productname,
												productversion: productResponse.data.productversion || '-',
											},
										];
									}

									// Find the highest productid (latest version) as default, but use current componentid if it's already selected
									let selectedVersionProductId = relation.componentid;
									if (versionList.length > 0) {
										const highestProductId = Math.max(...versionList.map(v => v.productid));
										// If current componentid is in the list, use it; otherwise use highest
										selectedVersionProductId = versionList.some(v => v.productid === relation.componentid)
											? relation.componentid
											: highestProductId;
									}

									return {
										...relation,
										version_list: versionList,
										selectedVersionProductId: selectedVersionProductId,
									};
								} catch (error) {
									return {
										...relation,
										version_list: [],
										selectedVersionProductId: relation.componentid,
									};
								}
							}
							return relation;
						})
					);
					setProjectRelations(updatedRelations);
				};

				fetchVersionLists();
			}
		}
	}, [p, setValue, privilegeLevels]);

	const { data: g } = useQuery({
		queryKey: ['project-group'],
		queryFn: () => getProductGroup({ max_size: true, order_by: 'groupname', status: 1 }),
	});

	const { data: c } = useQuery({
		queryKey: ['project-category'],
		queryFn: () => getProductCategory({ max_size: true, status: 1, order_by: 'categoryname' }),
	});

	const groups = useMemo(() => g?.data?.results || g?.data || [], [g]);
	const categories = useMemo(() => c?.data || null, [c]);

	const { mutate: updateProject, isPending } = usePatch({
		url: !isDuplicate ? `component/${project}` : `component`,
		method: !isDuplicate ? 'patch' : 'post',
		type: 'form-data',
		callback: (res) => {
			clearErrors();
			if (!isDuplicate && res.status === 200 && res.data?.productid) {
				toast.success(`project updated successfully`);
				const { productcodenumber, productversion, productid } = res.data;
				navigate(`/${route.project}/${productcodenumber}${productversion != '-' ? '_' + productversion : ''}/${productid}`);
				return;
			}
			if (isDuplicate && res.status === 201 && res.data.productid) {
				toast.success(`project created successfully`);
				const { productcodenumber, productversion, productid } = res.data;
				navigate(`/${route.project}/${productcodenumber}${productversion != '-' ? '_' + productversion : ''}/${productid}`);
				return;
			}
			if (res.data.failed) {
				setFilesAttachment((prevState) => {
					const currState = prevState;
					if ('index' in res.data) {
						currState[res.data.index].error = res.data.failed;
					}
					if ('already_uploaded_files' in res.data) {
						res.data.already_uploaded_files.forEach((r) => {
							const idx = currState.findIndex((f) => f.id && f.file.name === r.file_name);
							currState[idx].error = res.data.failed;
						});
					}
					return currState;
				});
				if ('already_uploaded_files' in res.data) {
					setIsFileExist(true);
				}
			}
		},
		onError: (err) => {
			if (err.data.error) {
				toast.error(`${err.status} - ${err.data.error}`);
				if (err.data.error === 'version exist!') {
					setError('productversion', { type: 'custom', message: 'Version exist' });
				}
			} else if (err?.data && Object.keys(err.data).length) {
				Object.keys(err.data).forEach((k) => {
					toast.error(err.data[k][0]);
				});
			} else {
				toast.error('something went wrong, please try again later');
			}
		},
		showError: true,
	});

	const onUploadBlob = async () => {
		const isUploadFile = filesAttachment?.filter((f) => !f.datasheetid);
		const res = await uploadBlob(isUploadFile);
		if (!res || res?.error) {
			setIsChecked(true);
			setIsLoading(false);
			return false;
		}
		return true;
	};

	const onSubmit = async (data) => {
		const isUploadFile = filesAttachment?.filter((f) => !f.datasheetid);
		if (isUploadFile.length > 0) {
			setIsLoading(true);
			if (!isChecked) {
				const isExist = await checkFileExist(isUploadFile);

				if (isExist.status) {
					// setFilesAttachment ln:214
					setFilesAttachment((prevState) => {
						const currState = prevState;

						isExist.response.forEach((r) => {
							const idx = currState.findIndex((f) => f.id && (f.id === r.id || f.file.name === r.name));
							if (r.status === 'exist') {
								currState[idx].error = 'Eksisterende fil';
							}
						});
						return currState;
					});
					setIsFileExist(true);
					setIsLoading(false);
					setIsChecked(true);
					return;
				}
			}

			const res = await onUploadBlob();
			if (!res) return;
		}

		const d = {
			...data,
			productprefixid: data.productprefixid.productprefixid,
			projectrelations: projectRelations?.map((p) => {
				// Use selectedVersionProductId if available, otherwise fallback to componentid or productid
				const componentId = p.selectedVersionProductId || p.componentid || p.productid;
				const obj = { componentcode: p.componentcode, amount: p.amount, componentid: componentId };
				if (!isDuplicate && p.id) obj.id = p.id;
				return obj;
			}),
		};
		if (d.productversion === p?.data?.productversion) {
			delete d.productversion;
		}
		if (!isDuplicate && filesAttachment.length) {
			d.attachments =
				filesAttachment.map((a) => {
					return {
						...a,
						linkdatasheet: a.linkdatasheet || a.file.name,
						privilegelevelid: a.privilegelevelid
							?.filter((f) => f.id !== 99)
							.filter((p) => p.value)
							.map((p) => p.id)
							.toString()
							.trim(),
						private: !!a.privilegelevelid?.find((p) => p.id === 99 && !!p.value),
						cover: 1,
					};
				}) || [];
			// const fUploads = filesAttachment.filter((a) => !a.datasheetid);
			// if (fUploads.length) d.files_uploaded = fUploads.map((f) => f.file);
		}
		if (!filesAttachment.length) {
			d.attachments = [];
		}

		if (data.reference?.length) {
			d.referencerelation = data.reference
				.filter((a) => a.reference_type && a.reference_detail)
				?.map((a) => {
					return { ...d, reference_id: d.id, reference_type: a.reference_type.trim(), reference_detail: a.reference_detail.trim() };
				});
		}

		const formData = cP(d, 'edit');
		updateProject(formData);
	};

	const validateFn = (file) => {
		if (!dType) return true;
		const docValidate = dType.validate;
		if (docValidate !== 0) return true;
		if (!file.includes('_')) return false;
		const name = file.substring(0, file.lastIndexOf('_'));
		const ind1 = name.substr(name.indexOf(' ') + 1);
		const fnName = ind1.substr(ind1.indexOf(' ') + 1);
		const ext = file.substring(file.lastIndexOf('.') + 1);
		const re = new RegExp('\\b' + dType + ' ' + number + ' ' + fnName + '_' + ver + '.' + ext + '\\b');
		return re.test(file);
	};

	const { getRootProps, getInputProps } = useDropzone({
		// accept: '*',
		multiple: true,
		onDrop: (acceptedFiles) => {
			const files = acceptedFiles.map((file, index) => {
				const id = uuidv4();
				const validate = validateFn(file.name);
				return Object.assign(
					{},
					{
						file,
						id: id,
						index: index,
						preview: URL.createObjectURL(file),
						privilegelevelid: [
							{ id: 1, name: 'Developer', value: true },
							{ id: 99, name: 'Private', value: true },
							{ id: 2, name: 'Administrasjon', value: true },
							{ id: 3, name: 'Produktsjef', value: true },
							{ id: 4, name: 'Prosjektleder', value: true },
							{ id: 5, name: 'Ansatt', value: true },
							{ id: 6, name: 'Public', value: true },
						],
						validated: validate,
						error: false,
						progress: null,
					}
				);
			});
			setFilesAttachment((prevState) => [...prevState].concat(files));
		},
	});

	const onChangePrivilege = (type, event, idx, index) => {
		setFilesAttachment((prevState) => {
			let currState = prevState;
			if (type === 'delete') {
				currState = currState.toSpliced(idx, 1);
			}
			if (type === 'update') {
				currState[idx].privilegelevelid[index].value = !event.target.checked;
			}
			return currState;
		});
		setTb(!tb);
	};

	const onSaveRelation = async (data) => {
		const updatedRelations = [];

		for (const relation of data) {
			if (relation.productid) {
				try {
					const productResponse = await getProduct(relation.productid);
					let versionList = productResponse?.data?.version_list || [];

					// Ensure current product is in version_list if not already present
					const currentProductInList = versionList.some(v => v.productid === relation.productid);
					if (!currentProductInList && productResponse?.data) {
						versionList = [
							...versionList,
							{
								productid: relation.productid,
								productname: productResponse.data.productname || relation.productname,
								productversion: productResponse.data.productversion || '-',
							},
						];
					}

					// Find the highest productid (latest version) as default
					let selectedVersionProductId = relation.productid;
					if (versionList.length > 0) {
						const highestProductId = Math.max(...versionList.map(v => v.productid));
						selectedVersionProductId = highestProductId;
					}

					updatedRelations.push({
						...relation,
						version_list: versionList,
						selectedVersionProductId: selectedVersionProductId,
					});
				} catch (error) {
					// Still add the relation even if fetch fails, but without version_list
					updatedRelations.push({
						...relation,
						version_list: [],
						selectedVersionProductId: relation.productid,
					});
				}
			} else {
				// If no productid, just add the relation as is
				updatedRelations.push({
					...relation,
					selectedVersionProductId: relation.productid,
				});
			}
		}

		setProjectRelations(updatedRelations);
	};

	const updateSelectedRelation = (r, type, config) => {
		setProjectRelations((prevState) => {
			let newSelectRelations = [...prevState];
			if (type === 'delete') {
				newSelectRelations.splice(r, 1);
			}
			if (type === 'update') {
				const index = r;
				if (index !== -1) {
					newSelectRelations[index] = { ...newSelectRelations[index], ...config };
				}
			}
			return newSelectRelations;
		});
	};
	const attachments = useMemo(() => filesAttachment, [filesAttachment]);
	const versions = p?.data?.version_list;
	return (
		<>
			<BDHelmet title='Update Project' description='BDS Samferdsel update project' />
			<div className='container flex flex-col gap-8 pt-8 pb-8 mx-auto'>
				{(isLoading || (isFetchingP && !isFetchedP) || isPending) && <Loader />}

				<div className='flex flex-col gap-2'>
					<h1 className='text-[2.5rem] font-medium'>Endre</h1>
					<span>Prosjekt / Produkt / komponent</span>
					<hr />
				</div>
				<form onSubmit={handleSubmit(onSubmit)} className='flex flex-col w-full gap-4'>
					<div className='grid grid-cols-12 gap-2'>
						<BaseAutocomplete
							control={control}
							name='productprefixid'
							label='Dokumenttype'
							errors={errors}
							options={productPrefix || []}
							optionLabel='productprefix'
							config={{ classes: 'col-span-3', multiple: false, inputProps: { required: true } }}
							callback={(_, dataOption) => {
								const { id, productprefix } = dataOption;
								if (id === 12 || productprefix.includes('BD-PROJ')) {
									setValue('productcomponentflag', '2');
								}
							}}
						/>
						<BaseInputValidation
							control={control}
							name='productcodenumber'
							label='Nummer'
							config={{ type: 'number', classes: 'col-span-2' }}
							errors={errors}
							required
						/>
						<BaseInputValidation
							control={control}
							name='productname'
							label='Produktnavn'
							config={{ classes: 'col-span-7' }}
							errors={errors}
							required
						/>
					</div>
					<div className='grid grid-cols-12 gap-2'>
						<BaseSelect
							control={control}
							name='category'
							label='Kategori'
							errors
							data={productCategory}
							config={{
								styles: {},
								classes: 'col-span-3',
								disabled: false,
								optionLabel: '--category type--',
								keyVal: 'id',
								keyName: 'categoryname',
								keyLabel: 'categoryname',
							}}
							size='small'
						/>
						{/* <BaseInputValidation
							control={control}
							name='article'
							label='Artikkel nummer'
							errors={errors}
							config={{ classes: 'col-span-9' }}
						/> */}
						<BaseInputValidation control={control} name='producturl' label='URL' errors={errors} config={{ classes: 'col-span-9' }} />
					</div>

					{/* <div className='flex gap-2'>
						<BaseInputValidation control={control} name='producturl' label='URL' errors={errors} />
					</div> */}

					{/* {vUrl && !isVideo && (
						<div style={{ height: '50vh', border: '1px solid rgba(0, 0, 0, 0.3)' }}>
							<PDFViewer url={vUrl} />
						</div>
					)} */}

					{vUrl && isVideo && videoUrl && (
						<div className='flex items-center justify-center w-full h-full m-auto'>
							<ReactPlayer url={videoUrl.url} controls={true} width='50svw' height='40svh' />
						</div>
					)}
					<div className='flex gap-2'>
						<BaseInputValidation control={control} name='description' label='Beskrivelse / informasjon' errors={errors} />
					</div>
					<div className='flex justify-between gap-24'>
						<div className='relative flex flex-col w-1/2 gap-2'>
							{fields.map((field, i) => {
								return (
									<Reference
										key={field.id}
										form={{ control, errors }}
										i={i}
										type='Remove'
										onClick={() => {
											let name = '';
											if (field.reference_type) name += field.reference_type;
											if (field.reference_detail) {
												if (field.reference_type) {
													name += ` - ${field.reference_detail}`;
												} else {
													name += field.reference_detail;
												}
											}
											setIsDeleteArticle({ name, index: i });
										}}
									/>
								);
							})}
							{!fields.length && (
								<Reference
									form={{ control, errors }}
									i={0}
									type='add'
									onClick={() => {
										append({ reference_type: '', reference_detail: '' });
									}}
								/>
							)}
							{fields.length > 0 && (
								<IconButton
									className='border !bg-primary !text-white !rounded-sm w-10 !absolute -right-9 top-[2px]'
									aria-label='add artikkelnummer'
									title='add artikkelnummer'
									onClick={() => {
										append({ reference_type: '', reference_detail: '' });
										trigger();
									}}
								>
									<Add />
								</IconButton>
							)}
						</div>
						{/* <div>
							<Controller
								name={'productcomponentflag'}
								defaultValue=''
								control={control}
								render={({ field }) => (
									<FormControl fullWidth>
										<RadioGroup
											{...field}
											// {...register('productcomponentflag')}
											row
											onChange={(_, value) => {
												field.onChange(value);
												const isProject = dType.id === 12 || dType.productprefix.includes('BD-PROJ');
												if (value !== 'project' && isProject) {
													setValue('productprefixid', '');
												}
											}}
											value={field.value}
										>
											{PTypes.map((option) => {
												return <FormControlLabel key={option.id} value={option.flag} control={<Radio />} label={option.label} />;
											})}
										</RadioGroup>
										<FormHelperText className='!text-red-600'>{String(errors['productcomponentflag']?.message ?? '')}</FormHelperText>
									</FormControl>
								)}
							/>
						</div> */}
						<div className='flex w-1/2 gap-2 p-0'>
							<BaseSelect
								control={control}
								name='producttype'
								label='Type'
								errors
								data={[
									{ id: 'standard', name: 'standard', value: '0' },
									{ id: 'non-standard', name: 'non-standard', value: '1' },
									{ id: 'arkiv', name: 'arkiv', value: '2' },
								]}
								config={{ styles: {}, classes: '!min-w-min', disabled: false, optionLabel: '--Velg type--' }}
								size='small'
							/>

							<BaseSelect
								control={control}
								name='status'
								label='Status'
								errors
								data={projectStatus}
								config={{
									styles: {},
									classes: '!min-w-min',
									disabled: false,
									optionLabel: '--Velg status--',
									keyVal: 'id_status',
									// keyName: isProj ? 'desc' : 'label',
									keyName: 'label',
									keyLabel: 'label',
								}}
								size='small'
							/>

							{versions?.length > 1 && pVer && (
								<FormControl variant='outlined' size='small' fullWidth>
									<InputLabel id='productversion'>Versjon</InputLabel>
									<Select
										defaultValue={pVer}
										labelId='productversion'
										className='bg-white min-w-28'
										label='Versjon'
										size='small'
										placeholder='--Select versjon--'
										onChange={() => {
											// const val = e.target.value;
											// setValue(val)
										}}
									>
										<MenuItem value=''>
											<em className='text-gray-300'>--Versjon--</em>
										</MenuItem>
										{versions?.map((p) => (
											<MenuItem key={p.productid} value={String(p.productversion)} name={p.productid.name}>
												{p.productversion}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
							{(!versions?.length || versions?.length === 1) && (
								<BaseInputValidation control={control} name='productversion' label='Versjon' errors={errors} />
							)}

							{/* <BaseSelect
								control={control}
								name='qrstatus'
								label='QR Link Status'
								errors
								data={[
									{ id: '0', name: 'Public', value: '0' },
									{ id: '1', name: 'Private', value: '1' },
								]}
								config={{ styles: {}, classes: '!min-w-min', disabled: false, optionLabel: '--Velg type--' }}
								size='small'
							/> */}
						</div>
					</div>

					<button className='mr-auto btn' onClick={() => setOpenRelation(true)} type='button'>
						Velg data til tildelte produkter / komponenter
					</button>
					{projectRelations?.length > 0 && (
						<div className='flex flex-wrap w-auto gap-4'>
							{projectRelations.map((r, i) => {
								const hasVersions = r.version_list && r.version_list.length > 0;
								const selectedVersionId = r.selectedVersionProductId || r.componentid || r.productid;

								return (
									<div
										key={`${r.id || r.productid}-${i}`}
										className='flex items-center justify-start gap-2 px-2 py-2 bg-white border border-black rounded-md w-fit'
									>
										<span>{r.component_prefix_name || r.productprefix}</span>
										<span>{r.component_code_number || r.productcodenumber}</span>
										<span>{r.component_productname || r.productname}</span>
										{hasVersions && (
											<Select
												value={selectedVersionId}
												onChange={(e) => {
													updateSelectedRelation(i, 'update', { selectedVersionProductId: e.target.value });
												}}
												size='small'
												className='min-w-32'
											>
												{r.version_list.map((version) => (
													<MenuItem key={version.productid} value={version.productid}>
														{version.productversion}
													</MenuItem>
												))}
											</Select>
										)}
										{/* <TextField
											defaultValue={r.amount || ''}
											onChange={(e) => updateSelectedRelation(i, 'update', { amount: e.target.value })}
											placeholder='amount'
											variant='standard'
											inputProps={{ style: { textAlign: 'center' } }}
										/> */}
										{/* <TextField
											defaultValue={r.componentcode || ''}
											onChange={(e) => updateSelectedRelation(i, 'update', { componentcode: e.target.value })}
											placeholder='code'
											variant='standard'
											inputProps={{ style: { textAlign: 'center' } }}
										/> */}
										<button type='button' onClick={() => updateSelectedRelation(i, 'delete')}>
											<Close fontSize='medium' sx={{ color: 'var(--primary)' }} />
										</button>
									</div>
								);
							})}
						</div>
					)}
					<div {...getRootProps({ className: 'dropzone w-fit flex flex-col gap-2 cursor-pointer' })}>
						<span>Last opp vedlegg</span>
						<div className='flex items-center gap-4'>
							<label className='flex items-center'>
								<input {...getInputProps()} />
								<button className='h-10 px-4 text-white border border-gray-700 rounded-sm bg-bd-blue' type='button'>
									<span>Velg filer</span>
								</button>
							</label>
							{filesAttachment.length > 0 ? (
								<span className='text-gray-700'>
									{filesAttachment.length} {filesAttachment.length === 1 ? 'file' : 'filer'}
								</span>
							) : (
								<span>Ingen fil valgt</span>
							)}
						</div>
					</div>

					{filesAttachment?.length > 0 && (
						<TableContainer className='w-full !max-w-[768px] xl:!w-1/2' component={Paper}>
							<Table sx={{ minWidth: 650 }} aria-label='simple table'>
								<TableHead>
									<TableRow>
										<TableCell>Filnavn</TableCell>
										<TableCell align='center'>QR kode</TableCell>
										{/* <TableCell align='center'>Administrasjon</TableCell>
										<TableCell align='center'>Produktsjef</TableCell>
										<TableCell align='center'>Prosjektleder</TableCell>
										<TableCell align='center'>Ansatt</TableCell>
										<TableCell align='center'>Public</TableCell> */}
										<TableCell align='center'>Action</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filesAttachment.map((row, idx) => (
										<TableRow
											className={twMerge(
												'relative',
												row.datasheetid ? '' : row.validated && !row.error ? 'border-2 border-green-500' : 'border-2 border-red-500'
											)}
											key={`${row.name}-${idx}`}
											sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
										>
											<TableCell component='th' scope='row' className='relative'>
												{!row.validated ? <span className='absolute text-red-500 top-1 left-4'>Wrong File Name</span> : null}
												{isLoading && !row.datasheetid && (
													<CircularProgress className='h-5 w-5 absolute left-[2px] top-[30px]' sx={{ color: '#00578d' }} size={12} />
												)}
												{attachments[idx].file?.name}
											</TableCell>
											{row.privilegelevelid?.map((p, i) => {
												if (p.id !== 99) return null;
												return (
													<TableCell key={[idx, i]} align='center'>
														<Checkbox
															onChange={(e) => onChangePrivilege('update', e, idx, i)}
															checked={!p.value}
															icon={<CircleOutlined />}
															checkedIcon={<Circle />}
														/>
													</TableCell>
												);
											})}

											<TableCell align='center'>
												<button type='button' onClick={(e) => onChangePrivilege('delete', e, idx)}>
													<Delete sx={{ color: 'var(--primary)' }} />
												</button>
											</TableCell>
											{row.error && (
												<span className='absolute bottom-0 text-red-500 left-4'>
													{`${row.error} ${'replace' in row ? (row.replace === 1 ? 'vil bli overskrevet' : 'vil ikke lastes opp') : ''}` ||
														'feil filnavn'}
												</span>
											)}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}

					<hr />
					<div className='flex gap-12 ml-auto'>
						<button type='button' className='btn min-w-32' onClick={() => setIsNewVersion(true)}>
							Ny Versjon
						</button>
						<div className='flex gap-2'>
							<button type='button' className='btn white min-w-32' onClick={() => navigate(-1)}>
								Avbryt
							</button>
							<button type='submit' className='btn min-w-32'>
								{isDuplicate ? 'Lagre' : 'Oppdater'}
							</button>
						</div>
					</div>

					{isNewVersion && (
						<ModalVersion
							open={!!isNewVersion}
							onSave={async (val) => {
								setValue('productversion', val);
								await trigger();
								handleSubmit(onSubmit);
							}}
							onClose={() => setIsNewVersion(false)}
						/>
					)}
				</form>
				{openRelation && (
					<ProjectRelation
						open={!!openRelation}
						onSave={onSaveRelation}
						relations={projectRelations}
						onClose={() => setOpenRelation(false)}
						productPrefix={productPrefix}
						groups={groups}
						categories={categories}
					/>
				)}
				{isFileExist && (
					<FileExist
						open={!!isFileExist}
						onSave={(res) => {
							setFilesAttachment(res);
							setIsFileExist(false);
						}}
						files={filesAttachment}
						onClose={() => setIsFileExist(false)}
					/>
				)}
			</div>
			{isDeleteArticle && (
				<ModalDelete
					open={!!isDeleteArticle}
					textWarning={'Er du sikker på at du vil slette dette artikkelnummeret?'}
					data={isDeleteArticle}
					onSave={() => {
						remove(isDeleteArticle.index);
						setIsDeleteArticle(false);
					}}
					onClose={() => setIsDeleteArticle(false)}
				/>
			)}
		</>
	);
}
